/**
 * Sortieren und Gruppieren für einfache Tabellen.
 *
 * `TaskListView` hat dieselbe Mechanik, ist aber über die modulweite Konstante
 * `COLUMNS`, die Fristgruppen und aufgabenspezifische Eigenschaften fest mit
 * Vorgängen verdrahtet. Statt sie dort herauszulösen – ein eigenes Vorhaben –
 * steht die reine Logik hier noch einmal, datengetrieben und ohne Fachbezug.
 *
 * Bewusst enthalten sind nur Sortierung und Gruppierung. Filter, Spaltenwahl
 * und Speichern der Ansicht bleiben `TaskListView` vorbehalten; wer das
 * braucht, hat eine Liste vor sich, die dessen Umfang rechtfertigt.
 */

import { useMemo, useState } from 'react';

export interface ViewColumn<T> {
  key: string;
  label: string;
  /** Rohwert für Sortierung und Gruppierung. */
  value: (row: T) => string | number | null;
  /** Beschriftung im Gruppenkopf, wenn sie vom Rohwert abweicht. */
  label_of?: (row: T) => string;
  groupable?: boolean;
  numeric?: boolean;
}

export interface TableGroup<T> {
  label: string | null;
  rows: T[];
}

export interface TableView<T> {
  sort: { key: string; dir: 'asc' | 'desc' } | null;
  toggleSort: (key: string) => void;
  groupBy: string;
  setGroupBy: (key: string) => void;
  groups: TableGroup<T>[];
  /** Für den Pfeil im Spaltenkopf: '↑' | '↓' | '↕'. */
  sortIndicator: (key: string) => string;
}

function labelOf<T>(column: ViewColumn<T>, row: T): string {
  if (column.label_of) return column.label_of(row);
  const raw = column.value(row);
  return raw === null || raw === '' ? '–' : String(raw);
}

export function useTableView<T>(rows: T[], columns: ViewColumn<T>[]): TableView<T> {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [groupBy, setGroupBy] = useState('');

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((entry) => entry.key === sort.key);
    if (!column) return rows;

    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const left = column.value(a);
      const right = column.value(b);
      if (column.numeric) return (Number(left ?? 0) - Number(right ?? 0)) * factor;
      return String(left ?? '').localeCompare(String(right ?? ''), 'de', { numeric: true }) * factor;
    });
  }, [rows, sort, columns]);

  const groups = useMemo<TableGroup<T>[]>(() => {
    if (!groupBy) return [{ label: null, rows: sorted }];
    const column = columns.find((entry) => entry.key === groupBy);
    if (!column) return [{ label: null, rows: sorted }];

    const map = new Map<string, T[]>();
    for (const row of sorted) {
      const label = labelOf(column, row);
      if (!map.has(label)) map.set(label, []);
      map.get(label)!.push(row);
    }

    const entries = [...map.entries()].map(([label, items]) => ({ label, rows: items }));
    if (column.numeric) {
      entries.sort((a, b) => Number(column.value(a.rows[0]) ?? 0) - Number(column.value(b.rows[0]) ?? 0));
    } else {
      entries.sort((a, b) => a.label.localeCompare(b.label, 'de'));
    }
    return entries;
  }, [sorted, groupBy, columns]);

  /** Dreizustand: aufsteigend → absteigend → ohne Sortierung. */
  const toggleSort = (key: string) =>
    setSort((current) => {
      if (current?.key !== key) return { key, dir: 'asc' };
      if (current.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });

  const sortIndicator = (key: string) => (sort?.key !== key ? '↕' : sort.dir === 'asc' ? '↑' : '↓');

  return { sort, toggleSort, groupBy, setGroupBy, groups, sortIndicator };
}
